import { useEffect, useState } from 'react';

interface UnifiedLoadingScreenProps {
  messages: string[];
  isCompleting?: boolean;
  /** Duration in ms for each message */
  messageDuration?: number;
}

export function UnifiedLoadingScreen({
  messages,
  isCompleting = false,
  messageDuration = 2000,
}: UnifiedLoadingScreenProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [progress, setProgress] = useState(8);

  useEffect(() => {
    setCurrentMessageIndex(0);
  }, [messages.length]);

  useEffect(() => {
    if (messages.length <= 1) return;

    const interval = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        setCurrentMessageIndex(prev => (prev + 1) % messages.length);
        setFade(true);
      }, 300);
    }, messageDuration);

    return () => clearInterval(interval);
  }, [messages.length, messageDuration]);

  useEffect(() => {
    if (isCompleting) {
      setProgress(100);
      return;
    }

    const startedAt = performance.now();
    setProgress(8);

    const interval = setInterval(() => {
      const elapsed = performance.now() - startedAt;
      const nextProgress = 8 + (1 - Math.exp(-elapsed / 2800)) * 84;

      setProgress(Math.min(92, nextProgress));
    }, 120);

    return () => clearInterval(interval);
  }, [isCompleting, messages.length]);

  const currentMessage = isCompleting
    ? 'Pemeriksaan selesai.'
    : (messages[currentMessageIndex] ?? messages[0] ?? 'Menyiapkan AsetQu Hub...');
  const roundedProgress = Math.round(progress);

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-[#f7faf9] px-5 text-slate-900'>
      <div className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#163EB7] via-[#009059] to-[#f0b429]' />
      <div className='absolute inset-0 bg-[linear-gradient(135deg,rgba(22,62,183,0.08),transparent_34%,rgba(0,144,89,0.08)_72%,transparent)]' />
      <div className='absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white to-transparent' />

      <div className='relative w-full max-w-[360px] text-center sm:max-w-[420px]'>
        <div className='mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-[28px] bg-white shadow-[0_24px_70px_rgba(15,23,42,0.14)] ring-1 ring-slate-200/80'>
          <div className='relative flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 ring-1 ring-slate-100'>
            <div className='absolute inset-0 rounded-full border-2 border-slate-100' />
            <img
              src='/logo2.png'
              alt='AsetQu Hub'
              className='h-8 w-8 object-contain'
              draggable={false}
            />
          </div>
        </div>

        <div className='mb-6 space-y-2'>
          <p className='text-xs font-semibold uppercase tracking-[0.22em] text-[#009059]'>
            AsetQu Hub
          </p>
          <h1 className='text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl'>
            Menyiapkan akses akun
          </h1>
        </div>

        <div className='mx-auto mb-7 w-full max-w-[280px]'>
          <div
            className='mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500'
            aria-hidden='true'
          >
            <span>Progress</span>
            <span>{roundedProgress}%</span>
          </div>
          <div
            role='progressbar'
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={roundedProgress}
            className='h-2 w-full overflow-hidden rounded-full bg-slate-200'
          >
            <div
              className='h-full rounded-full bg-gradient-to-r from-[#163EB7] via-[#009059] to-[#f0b429] transition-[width] duration-300 ease-out'
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className='flex min-h-[52px] items-center justify-center rounded-lg border border-slate-200/80 bg-white/80 px-4 py-3 shadow-sm backdrop-blur'>
          <p
            aria-live='polite'
            className={`text-sm font-medium leading-relaxed text-slate-700 transition-opacity duration-300 sm:text-base ${
              fade ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {currentMessage}
          </p>
        </div>

        {messages.length > 1 && !isCompleting && (
          <div className='mt-5 flex justify-center gap-2'>
            {messages.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentMessageIndex ? 'w-7 bg-[#163EB7]' : 'w-1.5 bg-slate-300'
                }`}
              />
            ))}
          </div>
        )}

        <p className='mt-8 text-xs font-medium text-slate-500'>Mohon tunggu sebentar</p>
      </div>
    </div>
  );
}
